import React, { useContext } from 'react';
import AvailabilityCodeIcon from '../../general/AvailabilityCodeIcon';
import { linkToNewDutyTransaction } from '../../../utils/dutyTransactions';
import { formatTimeOnlyShort } from '../../../utils/time';
import SkillIcon from '../../general/SkillIcon';
import { Dropdown } from '../../ui-kit';
import { IconTrash, IconPlus } from '../../ui-kit/icons';
import { DutyRequestIcon } from '../../../utils/dutyTransactions';
import { DutyPlannerContext } from './DutyPlanner';
import moment from 'moment';

const canUserPerformFunction = (func, interval) => {
  const functionSkillId = func.skill_id;
  if (!functionSkillId) return true;
  return (interval.skill_ids || []).includes(functionSkillId);
};

const ScheduleIntervalContextMenu = ({
  show,
  setShow,
  coordX,
  coordY,
  interval,
  time,
  availabilityCodes,
  updateIntervalById,
  splitInterval,
  removeInterval,
  openCommentsDialog
}) => {
  const handleClose = React.useCallback(() => setShow(false), [setShow]);

  const dutyPlannerContext = useContext(DutyPlannerContext);

  // hide context menu when clicking anywhere outside the dropdown
  React.useEffect(() => {
    if (!show) return;

    document.addEventListener('click', handleClose, false);
    return () => {
      document.removeEventListener('click', handleClose, false);
    };
  }, [show, handleClose]);

  const applyAvailabilityCode = code => {
    updateIntervalById(interval.id, {
      availability_code_id: code && code.id
    });
    handleClose();
  };

  const splitIntervalHandler = () => {
    splitInterval(interval.membership_id, 'membership', time);
    handleClose();
  };

  const assignAdditionalFunction = func => {
    const functionIds = interval.assigned_function_ids ? [...interval.assigned_function_ids, func.id] : [func.id];
    updateIntervalById(interval.id, {
      assigned_function_ids: functionIds
    });
    handleClose();
  };

  if (!show || !interval) return null; // also ensures no popup for duty requests

  const groupId = interval.group.split('_')[0];

  const groupSettings = dutyPlannerContext.stationSettings[groupId] || {};

  const { start_time, end_time, membership_id } = interval;
  const params = { start_time: moment(start_time).format(), end_time: moment(end_time).format() };
  const absenceRequestPath = linkToNewDutyTransaction('absence_requests', membership_id, params);

  const additionalFunctions = (
    groupSettings.allowMultipleFunctions
      ? groupSettings.functions.filter(f => !interval.assigned_function_ids.includes(f.id))
      : []
  ).filter(func => canUserPerformFunction(func, interval));

  return (
    <Dropdown.ContextMenu x={coordX} y={coordY} show={true}>
      {time && (
        <Dropdown.Item onClick={splitIntervalHandler}>
          {I18n.t('duty_planner.context_menu.split')} {formatTimeOnlyShort(time)}
        </Dropdown.Item>
      )}
      <Dropdown.Item onClick={openCommentsDialog}>{I18n.t('edit_comments')}</Dropdown.Item>
      <Dropdown.Divider />
      <Dropdown.SubMenu title={I18n.t('activerecord.models.schedule/availability_code.other')}>
        <Dropdown.Item onClick={() => applyAvailabilityCode(null)} key={'none'}>
          <i>{I18n.t('none')}</i>
        </Dropdown.Item>
        <Dropdown.Divider />
        {availabilityCodes.map(code => (
          <Dropdown.Item onClick={() => applyAvailabilityCode(code)} key={code.id}>
            <AvailabilityCodeIcon code={code} /> {code.name}
          </Dropdown.Item>
        ))}
      </Dropdown.SubMenu>
      {additionalFunctions.length > 0 && (
        <Dropdown.SubMenu Icon={IconPlus} title={I18n.t('activerecord.models.standby_duty_function')}>
          {additionalFunctions.map(func => (
            <Dropdown.Item onClick={() => assignAdditionalFunction(func)} key={func.id}>
              <SkillIcon skill={func} />
              {func.name}
            </Dropdown.Item>
          ))}
        </Dropdown.SubMenu>
      )}
      <Dropdown.Divider />
      <Dropdown.Item onClick={handleClose} href={absenceRequestPath} target="blank">
        <DutyRequestIcon type="absence_request" /> {I18n.t('schedules.standby_duty.actions.absence_request')}
      </Dropdown.Item>
      <Dropdown.Divider />
      <Dropdown.Item onClick={() => removeInterval(interval)}>
        <IconTrash /> {I18n.t('delete')}
      </Dropdown.Item>
      <Dropdown.Item onClick={handleClose}>{I18n.t('cancel')}</Dropdown.Item>
    </Dropdown.ContextMenu>
  );
};

export default ScheduleIntervalContextMenu;
